import { Dispatch, PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { AppDispatch, RootState } from './store';
import { BasketAPI, SaveState } from './types/api-types';
import { getBasket, getCartBaskets, getCommonElements, getCommonParts, getElements, getMaterials, getOptions, getParts, getServerMaterials, getShoppingCart } from './api-client';
import { CatalogRoot } from './types/catalog-internal';
import { combineElementsQueries, combineMaterialQueries } from './api-helpers';
import { VCoord, Viewer3DContent, WatchedElementResult } from '../3d-viewer/viewer-3d-content';
import { CompositionToViewerOptions, cartCompositionToViewer, makeCatalogKey } from './composition-to-viewer';
import { viewerActions } from './viewer-slice';
import { MAIN_SCENE } from './types/composition';
import { mainActions } from './main-slice';
import { basketActions } from './basket-slice';

const PADDING = 1;

export type GroupSpatialInfo = {
    basketId: number
    instance: number
    groupId?: string
    position: VCoord
    rotation: number
}

export type Cart3DIDReference = {
    basketId: number,
    instance: number,
    groupId?: string
}

export type Cart3DItem = {
    id: number,
    quantity: number,
    saveState: SaveState
}

export type Cart3D = {
    baskets: Cart3DItem[]
    catalogs: Record<string, CatalogRoot>
}

export type Basket3DState = {
    isLoading: boolean
    isSuccess: boolean
    isError: boolean
    cart?: Cart3D
    idReferences: Record<string, Cart3DIDReference>
    rootNodes: string[]
    layout: GroupSpatialInfo[]
    temporaryLoadLayout: GroupSpatialInfo[]
    layedOut: boolean
    selectedGroup?: Cart3DIDReference
}

const initialState: Basket3DState = {
    isLoading: false,
    isSuccess: false,
    isError: false,
    idReferences: {},
    rootNodes: [],
    layout: [],
    temporaryLoadLayout: [],
    layedOut: false
}

const CTV_OPTIONS_BASKET3D: CompositionToViewerOptions = {
    addUIControls: true,
    addAttachControls: false,
    allowElementSelection: false,
    allowGroupSelection: true,
    allowGroupMovement: true,
    showElementDimensions: false,
    showGroupDimensions: true,
    showGlobalDimensions: true,
    transparentBackground: false
};

const loadCart3DAsync = createAsyncThunk<LoadCart3DResult | undefined, number | undefined, { state: RootState }>(
    'basket3d/loadCart3D',
    async (cartId, thunkAPI) => {
        try {
            const state = thunkAPI.getState();
            const sessionId = state.main.sessionId;
            const lang = state.main.lang;
            if (sessionId && cartId && !state.basket3D.isLoading && !state.basket3D.isSuccess && !state.basket3D.isError) {
                thunkAPI.dispatch(basketActions.editCart(cartId));
                thunkAPI.dispatch(basket3DActions.startLoading())            
                return loadCart3D(cartId, sessionId, lang);
            } else {
                return undefined
            }
        } catch (err) {
            console.error(err);
            throw err
        }
    }
)

const selectGroup = (groupId: string | undefined) => {
    return (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const cart = state.basket3D.cart;
        if (groupId) {
            const selectedReference = state.basket3D.idReferences[groupId];
            dispatch(basket3DActions._selectGroup(selectedReference));
            if (cart) {
                const [viewerContent, idReferences] = cartCompositionToViewer(
                    cart?.baskets,
                    cart?.catalogs,

                    CTV_OPTIONS_BASKET3D,
                    selectedReference?.basketId,
                    selectedReference?.instance,
                    selectedReference?.groupId,
                    state.basket3D.layout,

                    '/models', '/materials',
                    '/environment/dancing-hall'
                )
                dispatch(viewerActions.setContent(viewerContent));
            }
        } else {
            dispatch(basket3DActions._selectGroup(undefined));
            if (cart) {
                const [viewerContent, idReferences] = cartCompositionToViewer(
                    cart?.baskets,
                    cart?.catalogs,

                    CTV_OPTIONS_BASKET3D,
                    undefined, undefined, undefined,
                    state.basket3D.layout,

                    '/models', '/materials',
                    '/environment/dancing-hall'
                )
                dispatch(viewerActions.setContent(viewerContent));
            }
        }
    }
}

const setAutoLayout = (layout: AutoLayoutResult[]) => {

    return (dispatch: Dispatch, getState: () => RootState) => {

        dispatch(basket3DActions._setAutoLayout(layout));
        let state = getState();

        const cart = state.basket3D.cart;
        const selectedReference = state.basket3D.selectedGroup;
        if (cart) {
            const [viewerContent, idReferences] = cartCompositionToViewer(
                cart?.baskets,
                cart?.catalogs,

                CTV_OPTIONS_BASKET3D,
                selectedReference?.basketId,
                selectedReference?.instance,
                selectedReference?.groupId,
                state.basket3D.layout,

                '/models', '/materials',
                '/environment/dancing-hall'
            )
            dispatch(viewerActions.setContent(viewerContent));
        }

    }

}

const rotateGroup = (groupId: string) => {

    return (dispatch: AppDispatch, getState: () => RootState) => {

        let state = getState();
        const idReference = state.basket3D.idReferences[groupId];
        if (!idReference) return;

        const layout = state.basket3D.layout.find(li => li.basketId === idReference.basketId &&
            li.instance === idReference.instance &&
            li.groupId === idReference.groupId);
        if (!layout) {
            dispatch(basket3DActions._updateGroupLayout({
                basketId: idReference.basketId,
                instance: idReference.instance,
                groupId: idReference.groupId,
                position: [0, 0, 0],
                rotation: 90
            }));
        } else {
            dispatch(basket3DActions._updateGroupLayout({
                ...layout,
                rotation: (layout.rotation + 90) % 360
            }));
        }

        state = getState();
        const cart = state.basket3D.cart;
        const selectedReference = state.basket3D.selectedGroup;
        if (cart) {
            const [viewerContent, idReferences] = cartCompositionToViewer(
                cart?.baskets,
                cart?.catalogs,

                CTV_OPTIONS_BASKET3D,
                selectedReference?.basketId,
                selectedReference?.instance,
                selectedReference?.groupId,
                state.basket3D.layout,

                '/models', '/materials',
                '/environment/dancing-hall'
            )
            dispatch(viewerActions.setContent(viewerContent));
        }

    }

}


const moveGroup = (groupId: string, position: VCoord) => {

    return (dispatch: AppDispatch, getState: () => RootState) => {

        let state = getState();
        const idReference = state.basket3D.idReferences[groupId];
        if (!idReference) return;

        const layout = state.basket3D.layout.find(li => li.basketId === idReference.basketId &&
            li.instance === idReference.instance &&
            li.groupId === idReference.groupId);
        if (!layout) {
            dispatch(basket3DActions._updateGroupLayout({
                basketId: idReference.basketId,
                instance: idReference.instance,
                groupId: idReference.groupId,
                position: position,
                rotation: 0
            }));
        } else {
            dispatch(basket3DActions._updateGroupLayout({
                ...layout,
                position
            }));
        }

        state = getState();
        const cart = state.basket3D.cart;
        const selectedReference = state.basket3D.selectedGroup;
        if (cart) {
            const [viewerContent, idReferences] = cartCompositionToViewer(
                cart?.baskets,
                cart?.catalogs,

                CTV_OPTIONS_BASKET3D,
                selectedReference?.basketId,
                selectedReference?.instance,
                selectedReference?.groupId,
                state.basket3D.layout,

                '/models', '/materials',
                '/environment/dancing-hall'
            )
            dispatch(viewerActions.setContent(viewerContent));
        }

    }

}


export const basket3DSlice = createSlice({
    name: 'basket3d',
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
        },
        _setAutoLayout(state, action: PayloadAction<AutoLayoutResult[]>) {
            state.layout = action.payload.map(autoResult => {
                const idReference = state.idReferences[autoResult.groupId]!;
                return {
                    basketId: idReference.basketId,
                    instance: idReference.instance,
                    groupId: idReference.groupId,
                    position: autoResult.position,
                    rotation: autoResult.rotation
                }
            })

            state.layedOut = true;
        },
        _selectGroup(state, action: PayloadAction<Cart3DIDReference | undefined>) {
            state.selectedGroup = action.payload;
        },
        _updateGroupLayout(state, action: PayloadAction<GroupSpatialInfo>) {
            const layoutItem = state.layout.find(li => li.basketId === action.payload.basketId &&
                li.instance === action.payload.instance &&
                li.groupId === action.payload.groupId);
            if (layoutItem) {
                layoutItem.position = action.payload.position;
                layoutItem.rotation = action.payload.rotation;
            } else {
                state.layout.push(action.payload);
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(mainActions.enterPage, (state, action) => {
            if (action.payload !== 'cart3d') {
                state.isLoading = initialState.isLoading;
                state.isSuccess = initialState.isSuccess;
                state.isError = initialState.isError;
                state.cart = initialState.cart;
                state.idReferences = initialState.idReferences;
                state.rootNodes = initialState.rootNodes;
                state.layout = initialState.layout;
                state.layedOut = initialState.layedOut;
                state.selectedGroup = initialState.selectedGroup;
            }
        })
        .addCase(loadCart3DAsync.fulfilled, (state, action) => {
            if (action.payload) {
                state.isLoading = false;
                state.isSuccess = true;
                state.isError = false;
                state.cart = action.payload.cart;
                state.idReferences = action.payload.idReferences;
                state.rootNodes = action.payload.viewerContent?.watchedNodes ?? [];
                state.temporaryLoadLayout = action.payload.existingLayout;
                state.layout = [...action.payload.existingLayout];
            }
        })
        .addCase(loadCart3DAsync.rejected, (state) => {
            state.isLoading = false;
            state.isSuccess = false;
            state.isError = true;
        })

    }
});


export const basket3DActions = {
    ...basket3DSlice.actions,
    loadCart3D: loadCart3DAsync,
    selectGroup,
    setAutoLayout,
    rotateGroup,
    moveGroup
}

export function selectCart3D(state: RootState) { return state.basket3D.cart; }


export default basket3DSlice.reducer;

export type AutoLayoutResult = {
    groupId: string
    position: VCoord
    rotation: number
}

export function autoLayoutBasket3D(
    rootNodes: string[],
    elementDimensions: Record<string, WatchedElementResult>,
    content: Viewer3DContent
): AutoLayoutResult[] {

    const square = Math.ceil(Math.sqrt(rootNodes.length));
    const cols = square;
    const rows = Math.ceil(rootNodes.length / cols);

    let maxWidth = 0;
    let maxDepth = 0;

    for (const node of rootNodes) {
        const nodeDimensions = elementDimensions[node]!.dimensions;
        maxWidth = Math.max(maxWidth, nodeDimensions[0]);
        maxDepth = Math.max(maxDepth, nodeDimensions[1]);
    }

    const totalWidth = (maxWidth * cols) + (PADDING * (cols - 1));
    const totalDepth = (maxDepth * rows) + (PADDING * (rows - 1));
    const baseX = -(totalWidth / 2);
    const baseZ = -(totalDepth / 2);

    const nodesInContent = content!.scenes[MAIN_SCENE].content.items;
    const result: AutoLayoutResult[] = [];

    let row = 0;
    let col = 0;
    for (const node of rootNodes) {

        const x = baseX + (col * (maxWidth + PADDING)) + (maxWidth / 2);
        const z = baseZ + (row * (maxDepth + PADDING)) + (maxDepth / 2);
        const nodeInContent = nodesInContent.find(n => n.id === node);

        if (nodeInContent && 'items' in nodeInContent) {
            const position = nodeInContent.position.position;
            if (Array.isArray(position)) {
                result.push({
                    groupId: node,
                    position: [x, position[1], z],
                    rotation: 0
                })
            }
        }

        col += 1;
        if (col === cols) {
            row += 1;
            col = 0;
        }
    }

    return result;
}

export function autoLayoutBasket3DWithPrevious(
    rootNodes: string[],
    elementDimensions: Record<string, WatchedElementResult>,
    content: Viewer3DContent,
    idReferences: Record<string, Cart3DIDReference>,
    previous: GroupSpatialInfo[]
): AutoLayoutResult[] {

    const result: AutoLayoutResult[] = [];
    const newNodes = [];
    let minZ = Number.MAX_VALUE;
    let maxX = Number.MIN_VALUE;
    let minX = Number.MAX_VALUE;
    let neededX = 0;
    let maxNewNodeZ = 0;

    for(const node of rootNodes) {

        const reference = idReferences[node];
        const prevLayout = reference.groupId ?
            previous.find(p => p.basketId === reference.basketId &&
                               p.instance === reference.instance &&
                               p.groupId === reference.groupId)
            :
            previous.find(p => p.basketId === reference.basketId &&
                               p.instance === reference.instance);

        const dimensions = elementDimensions[node];
        if(prevLayout) {
            result.push({
                groupId: node,
                position: prevLayout.position,
                rotation: prevLayout.rotation
            })
            maxX = Math.max(maxX, prevLayout.position[0]);
            minX = Math.min(minX, prevLayout.position[0]);
            if(dimensions) {
                minZ = Math.min(minZ, prevLayout.position[2] - dimensions.boundingBox[0][2]);                
            }
        } else {
            newNodes.push(node);
            neededX += dimensions.boundingBox[1][0] - dimensions.boundingBox[0][0];
            maxNewNodeZ = Math.max(maxNewNodeZ, dimensions.boundingBox[1][2] - dimensions.boundingBox[0][2]);
        }

    }

    let x = ( (maxX + minX) / 2) // center
            - 
            ( ( neededX + ( (newNodes.length - 1) * PADDING )) / 2);
    const z = minZ - (PADDING * 2) - (maxNewNodeZ/2);

    for(const node of newNodes) {
        result.push({
            groupId: node,
            position: [ x, 0, z ],
            rotation: 0
        });
        const dimensions = elementDimensions[node];
        x += PADDING + (dimensions.boundingBox[1][0] - dimensions.boundingBox[0][0]);
    }

    return result;

}

// ==== additional logic ====

export type LoadCart3DResult = {
    cart: Cart3D,
    viewerContent: Viewer3DContent | undefined,
    idReferences: Record<string, Cart3DIDReference>
    existingLayout: GroupSpatialInfo[]
}

export async function loadCart3D(cartId: number, sessionId: string, lang: string): Promise<LoadCart3DResult> {
    const commonElementsP = getCommonElements(lang);
    const commonPartsP = getCommonParts();
    const materialsP = getMaterials(lang);
    const serverMaterialsP = getServerMaterials(lang, sessionId);
    const cartStateP = getShoppingCart(cartId, sessionId);
    const cart3DItemsP = getCart3DSaveStates(cartId, sessionId);
    const catalogs: Record<string, CatalogRoot> = {}

    const [commonElements, commonParts, assetMaterials, serverMaterials, cartState, cart3DItems] = await Promise.all(
        [commonElementsP, commonPartsP, materialsP, serverMaterialsP, cartStateP, cart3DItemsP]);
    const { tags: materialTags, materials: qualities, colors } = combineMaterialQueries(serverMaterials, assetMaterials);
    for (const cart3DItem of cart3DItems) {
        const catalogKey = makeCatalogKey(cart3DItem.saveState.family, cart3DItem.saveState.variant);
        if (!catalogs[catalogKey]) {
            const [options, familyElements, familyParts] = await getFamilyCatalog(cart3DItem.saveState.family, cart3DItem.saveState.variant, lang);
            const parts = commonParts.concat(familyParts);
            const { elements, elementParts } = combineElementsQueries(familyElements, commonElements);
            catalogs[catalogKey] = {
                materialTags,
                qualities,
                colors,

                compose: familyElements.compose ?? false,
                parts,
                elements,
                elementParts,

                ...options,

                elementsRoot: {
                    compose: familyElements.compose ?? false,
                    elements,
                    elementParts
                }
            }
        }
    }

    let existingLayout: GroupSpatialInfo[] = [];
    if (cartState.configurationstate) {

        // if there was a previous state, we try to "adapt to new things" here
        // if not, we left it empty and there's a "relayout" process later

        let cartStateJS = JSON.parse(cartState.configurationstate);
        if ('layout' in cartStateJS) {
            const loadedLayout = cartStateJS.layout;

            for(const group of loadedLayout) {
                // older snapshots may contain basketId/instance as strings, normalize
                const normalizedGroup: GroupSpatialInfo = {
                    ...group,
                    basketId: Number(group.basketId),
                    instance: Number(group.instance)
                };
                const cartItem = cart3DItems.find(basket => basket.id === normalizedGroup.basketId);
                if(cartItem) {
                    // count instances
                    if(normalizedGroup.instance < cartItem.quantity) {
                        existingLayout.push(normalizedGroup);
                    }
                    // else discard it, there are less items in the cart
                }
                // else discard it, that basket item is no longer
            }
        }
    }

    const cart: Cart3D = { baskets: cart3DItems, catalogs };
    const [viewerContent, idReferences] = cartCompositionToViewer(
        cart.baskets,
        cart.catalogs,

        CTV_OPTIONS_BASKET3D,
        undefined, undefined, undefined,
        existingLayout,

        // TODO avoid this constant, but now is a React context :-(
        '/models', '/materials',
        '/environment/dancing-hall'
    )

    return { cart, viewerContent, idReferences, existingLayout }
}

// ==== helpers ====

async function getCart3DSaveStates(cartId: number, sessionId: string): Promise<Cart3DItem[]> {
    const apiBaskets = await getCartBaskets(cartId, sessionId);
    if (apiBaskets.SUCCESS) {
        const saveStates = await Promise.all(apiBaskets.shoppingbaskets.map(async (apiBasket: BasketAPI) => {
            const apiBasketDetail = await getBasket(apiBasket.shoppingbasketID, sessionId);
            if (apiBasketDetail.SUCCESS) {
               // success = true;
            } else {
                console.log(`Retrying fetching basket ${apiBasket.shoppingbasketID}`);
                // Add a delay before retrying
                await new Promise(resolve => setTimeout(resolve, 500)); // 1 second delay
            }
            if (apiBasketDetail.SUCCESS && apiBasketDetail.state !== '') {
                return {
                    // the API may deliver numbers as strings, normalize to compare reliably
                    id: Number(apiBasket.shoppingbasketID),
                    quantity: Number(apiBasket.quantity),
                    saveState: JSON.parse(apiBasketDetail.state)
                }
            } else {
                throw new Error(`Couldn't get basket content`);
            }
        }));
        return saveStates;
    } else {
        throw new Error(`Couldn't get baskets`);
    }

}

async function getFamilyCatalog(family: string, variant: string | undefined, lang: string) {

    const optionsP = getOptions(family, variant, lang);
    const elementsP = getElements(family, variant, lang);
    const partsP = getParts(family, variant);

    return Promise.all([optionsP, elementsP, partsP]);

}

